import React, { useState, useEffect, useRef } from 'react';
import './ComplianceActivityDrawer.css';
import iconEdit     from './assets/cl2-icon-edit.svg';
import iconDownload from './assets/cl2-icon-download.svg';
import { hasUserAccess } from '../../../../backend/auth';
import { USER_PRIVILEGES } from '../../UserManagement/Roles/RoleConfiguration';
import {
    useGetComplianceActivityDocuments,
    useUploadDocument,
    useDeleteComplianceDocument,
    useUpdateComplianceSchedule,
    useRecallCompliance,
} from '../../../../backend/compliance';
import { downloadWithPresignedUrl } from '../../../../utils/common';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { validateFile, FILE_TYPE_GROUPS, FILE_SIZE_LIMITS, generateAcceptAttribute, VALIDATION_MESSAGES } from '../../../../utils/fileValidation';

// ── Types ──────────────────────────────────────────────────────────────────────
type DrawerTab  = 'review' | 'act' | 'rule' | 'activity';
type DrawerMode = 'view' | 'edit';

interface Props {
    activity:     any | null;   // full row from the listing (includes nested act/rule/activity)
    initialMode?: 'view' | 'edit';  // 'view' for eye button, 'edit' for pencil button
    onClose:      () => void;
    onRefresh?:   () => void;  // called after a successful submit so the list re-fetches
}

// ── Constants ─────────────────────────────────────────────────────────────────
const TABS: { key: DrawerTab; label: string }[] = [
    { key: 'review',   label: 'Review & Evidences' },
    { key: 'act',      label: 'Act'                },
    { key: 'rule',     label: 'Rule'               },
    { key: 'activity', label: 'Activity'           },
];

// CSV excluded: compliance backend rejects CSV (returns "Invalid file extension").
const UPLOAD_ALLOWED_TYPES = FILE_TYPE_GROUPS.COMPLIANCE_DOCUMENTS;
const UPLOAD_MAX_SIZE       = FILE_SIZE_LIMITS.LARGE;    // 25 MB
const UPLOAD_ACCEPT         = generateAcceptAttribute(UPLOAD_ALLOWED_TYPES);
const UPLOAD_HINT           = VALIDATION_MESSAGES.ALLOWED_FILE_COMPLIANCE_DOCS;

const REVIEW_STATUS_OPTIONS = [
    { value: '',          label: '— Select status —' },
    { value: 'Approved',  label: 'Approved'           },
    { value: 'Rejected',  label: 'Rejected'           },
];

const STATUS_LABELS: Record<string, string> = {
    Due:          'Upcoming',
    NonCompliant: 'Non-Compliant',
    Submitted:    'Approval Pending',
    Approved:     'Compliant',
    Rejected:     'Rejected',
    Recalled:     'Recalled',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Compute priority label + sub-label from due_date — mirrors listing renderPriority logic */
function renderPriorityBadge(dueDate: string | null | undefined): React.ReactNode {
    if (!dueDate) return <span style={{ color: '#757575' }}>—</span>;
    const today    = dayjs().startOf('day');
    const due      = dayjs(dueDate).startOf('day');
    const daysLeft = due.diff(today, 'day');
    let label: string;
    let color: string;
    if (daysLeft <= 15) {
        label = 'Critical'; color = '#ec221f';
    } else if (daysLeft <= 20) {
        label = 'Medium';   color = '#2965ad';
    } else {
        label = 'Low';      color = '#757575';
    }
    const daysText = daysLeft < 0
        ? Math.abs(daysLeft) + ' day' + (Math.abs(daysLeft) === 1 ? '' : 's') + ' overdue'
        : daysLeft + ' day' + (daysLeft === 1 ? '' : 's') + ' left';
    return (
        <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span style={{ color, fontWeight: 700, fontSize: 13 }}>{label}</span>
            <span style={{ color: '#9ca3af', fontSize: 11, fontWeight: 400 }}>{daysText}</span>
        </span>
    );
}

function titleColorClass(status: string): string {
    switch ((status || '').toLowerCase()) {
        case 'due':          return 'cad-title-due';           /* Upcoming        — amber      */
        case 'noncompliant': return 'cad-title-noncompliant';  /* Non-Compliant   — red        */
        case 'submitted':    return 'cad-title-submitted';     /* Approval Pending — near-black */
        case 'approved':     return 'cad-title-approved';      /* Compliant       — green      */
        case 'rejected':     return 'cad-title-rejected';      /* Rejected        — brown      */
        case 'recalled':     return 'cad-title-recalled';      /* Recalled        — orange     */
        default:             return '';
    }
}

function statusBadgeClass(status: string): string {
    switch ((status || '').toLowerCase()) {
        case 'due':          return 'cad-status-badge-due';
        case 'noncompliant': return 'cad-status-badge-noncompliant';
        case 'submitted':    return 'cad-status-badge-submitted';
        case 'approved':     return 'cad-status-badge-approved';
        case 'rejected':     return 'cad-status-badge-rejected';
        case 'recalled':     return 'cad-status-badge-recalled';
        default:             return 'cad-status-badge-default';
    }
}

function getStr(obj: any, ...keys: string[]): string {
    let cur = obj;
    for (const k of keys) {
        if (!cur || typeof cur !== 'object') return '—';
        cur = cur[k];
    }
    return (cur === null || cur === undefined || cur === '') ? '—' : String(cur);
}

function formatDate(val: string | null | undefined): string {
    if (!val) return '—';
    const d = dayjs(val);
    return d.isValid() ? d.format('DD-MM-YYYY') : '—';
}

function formatBool(val: any): string {
    if (val === true  || val === 'true'  || val === 1) return 'Yes';
    if (val === false || val === 'false' || val === 0) return 'No';
    return '—';
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function ComplianceActivityDrawer({ activity, initialMode, onClose, onRefresh }: Props) {
    const isOwner   = hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD);
    const isManager = hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD);

    const canOwnerEdit    = isOwner   && activity && (activity.status === 'Due' || activity.status === 'NonCompliant' || activity.status === 'Recalled' || activity.status === 'Rejected');
    const canManagerEdit  = isManager && activity && activity.status === 'Submitted';
    const canManagerRecall = isManager && activity && activity.status === 'Approved';
    const canEdit          = canOwnerEdit || canManagerEdit || canManagerRecall;

    // ── Local state ────────────────────────────────────────────────────────────
    const [activeTab,     setActiveTab]     = useState<DrawerTab>('review');
    const [mode,          setMode]          = useState<DrawerMode>('view');
    const [auditedDate,   setAuditedDate]   = useState('');
    const [ownerComment,  setOwnerComment]  = useState('');
    const [reviewStatus,  setReviewStatus]  = useState('');
    const [auditRemarks,  setAuditRemarks]  = useState('');
    // Recall state (manager only, Approved activities)
    const [recallRemarks,      setRecallRemarks]      = useState('');
    const [recallDueDate,      setRecallDueDate]      = useState('');
    // Confirmation dialog state (owner submit)
    const [showSubmitConfirm,  setShowSubmitConfirm]  = useState(false);
    // Tracks the in-flight upload so we can show a "Uploading…" row in the evidence list
    const [pendingFileName,    setPendingFileName]    = useState('');
    const fileRef = useRef<HTMLInputElement>(null);
    // Distinguishes the manager's "Save" (Actual Completion Date update on an
    // already-Approved activity, no status change) from a real submit/review,
    // so the shared updateComplianceSchedule success handler can toast correctly.
    const isManagerDateUpdateRef = useRef(false);

    // Reset form when drawer opens with a new activity
    useEffect(() => {
        if (activity) {
            setActiveTab('review');
            setMode(initialMode || 'view');
            setAuditedDate(activity.audited_date ? dayjs(activity.audited_date).format('YYYY-MM-DD') : '');
            setOwnerComment(activity.forms_status_remarks || '');
            setReviewStatus('');
            setAuditRemarks(activity.audit_remarks || '');
            setRecallRemarks('');
            setRecallDueDate(activity && activity.due_date ? dayjs(activity.due_date).format('YYYY-MM-DD') : '');
        }
    }, [activity && activity.id, initialMode]);

    // ── API hooks ──────────────────────────────────────────────────────────────
    const { documents, isFetching: docsLoading, refetch: refetchDocs } =
        useGetComplianceActivityDocuments(
            activity ? { filters: { compliance_id_eq: activity.id } } : {}
        );

    const { uploadDocument, uploading } = useUploadDocument(
        () => { setPendingFileName(''); refetchDocs(); toast.success('File uploaded successfully'); },
        (error: any) => {
            setPendingFileName('');
            const data = error && error.response && error.response.data;
            const msg  = (data && data.error) || 'Upload failed. Please try again.';
            toast.error(msg);
        }
    );

    const { deleteDocument, deleting } = useDeleteComplianceDocument(
        () => { refetchDocs(); toast.success('File removed'); },
        () => { toast.error('Could not delete file. Please try again.'); }
    );

    const { updateComplianceSchedule, updating } = useUpdateComplianceSchedule(
        () => {
            if (isManagerDateUpdateRef.current) {
                toast.success('Activity Updated Successfully');
            } else {
                toast.success(isOwner ? 'Activity submitted successfully' : 'Review submitted successfully');
            }
            isManagerDateUpdateRef.current = false;
            if (onRefresh) onRefresh();
            onClose();
        },
        () => { isManagerDateUpdateRef.current = false; toast.error('Submit failed. Please try again.'); }
    );

    const { recallCompliance, recalling } = useRecallCompliance(
        () => {
            toast.success('Compliance activity recalled successfully');
            if (onRefresh) onRefresh();
            onClose();
        },
        () => { toast.error('Recall failed. Please try again.'); }
    );

    // ── Handlers ───────────────────────────────────────────────────────────────
    function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files && e.target.files[0];
        // Reset input so same file can be re-selected even after rejection
        if (fileRef.current) fileRef.current.value = '';
        if (!file || !activity) return;
        const validation = validateFile(file, UPLOAD_ALLOWED_TYPES, UPLOAD_MAX_SIZE);
        if (!validation.isValid) {
            toast.error(validation.error || 'Invalid file');
            return;
        }
        const fd = new FormData();
        fd.append('file', file, file.name);
        fd.append('compliance_id', String(activity.id));
        setPendingFileName(file.name);
        uploadDocument({ formData: fd });
    }

    function handleFileDrop(e: React.DragEvent<HTMLDivElement>) {
        e.preventDefault();
        const file = e.dataTransfer.files && e.dataTransfer.files[0];
        if (!file || !activity) return;
        const validation = validateFile(file, UPLOAD_ALLOWED_TYPES, UPLOAD_MAX_SIZE);
        if (!validation.isValid) {
            toast.error(validation.error || 'Invalid file');
            return;
        }
        const fd = new FormData();
        fd.append('file', file, file.name);
        fd.append('compliance_id', String(activity.id));
        setPendingFileName(file.name);
        uploadDocument({ formData: fd });
    }

    // Guard: validate required fields then open the confirmation dialog
    function handleOwnerSubmit() {
        if (!activity) return;
        if (!ownerComment.trim()) { toast.error('Please enter Comments for Compliance Manager'); return; }
        if (auditedDate) {
            const today = dayjs().format('YYYY-MM-DD');
            if (auditedDate > today) { toast.error('Actual Completion Date cannot be a future date'); return; }
        }
        setShowSubmitConfirm(true);
    }

    // Actual submit — called only after the user confirms in the dialog
    function handleConfirmOwnerSubmit() {
        if (!activity) return;
        setShowSubmitConfirm(false);
        updateComplianceSchedule({
            compliance: {
                id:                    activity.id,
                status:                'Submitted',
                audited_date:          auditedDate,
                forms_status_remarks:  ownerComment.trim() || undefined,
            },
        });
    }

    function handleManagerSubmit() {
        if (!activity) return;
        if (!reviewStatus) { toast.error('Please select Compliance Status'); return; }
        if (!auditedDate) { toast.error('Please enter the Actual Completion Date'); return; }
        const today = dayjs().format('YYYY-MM-DD');
        if (auditedDate > today) { toast.error('Actual Completion Date cannot be a future date'); return; }
        if (!auditRemarks.trim()) { toast.error('Please enter Remarks'); return; }
        updateComplianceSchedule({
            compliance: {
                id:            activity.id,
                status:        reviewStatus,
                audited_date:  auditedDate,
                audit_remarks: auditRemarks.trim(),
            },
        });
    }

    function handleManagerRecall() {
        if (!activity) return;
        if (!recallDueDate) { toast.error('Please enter a new Due Date'); return; }
        if (!recallRemarks.trim()) { toast.error('Recall Remarks are required'); return; }
        recallCompliance({
            id:             activity.id,
            recall_remarks: recallRemarks.trim(),
            due_date:       recallDueDate,
        });
    }

    // Manager updating Actual Completion Date on an already-Approved activity
    // (without changing the Approved status or triggering a recall).
    function handleManagerUpdateDate() {
        if (!activity) return;
        if (!auditedDate) { toast.error('Please enter the Actual Completion Date'); return; }
        const today = dayjs().format('YYYY-MM-DD');
        if (auditedDate > today) { toast.error('Actual Completion Date cannot be a future date'); return; }
        isManagerDateUpdateRef.current = true;
        updateComplianceSchedule({
            compliance: {
                id:           activity.id,
                status:       'Approved',
                audited_date: auditedDate,
            },
        });
    }

    function handleCancel() {
        // Reset to last-saved values
        setAuditedDate(activity && activity.audited_date ? dayjs(activity.audited_date).format('YYYY-MM-DD') : '');
        setOwnerComment((activity && activity.forms_status_remarks) || '');
        setReviewStatus('');
        setAuditRemarks((activity && activity.audit_remarks) || '');
        setRecallRemarks('');
        setRecallDueDate(activity && activity.due_date ? dayjs(activity.due_date).format('YYYY-MM-DD') : '');
        setMode('view');
    }

    const submitting  = (updating as boolean) || (recalling as boolean) || false;
    const isEditMode  = mode === 'edit' && canEdit;

    // For an Approved activity, the manager's "Save" only updates the Actual
    // Completion Date (no status change) — keep it disabled until that date
    // actually differs from what's currently on the activity.
    const originalAuditedDate = activity && activity.audited_date ? dayjs(activity.audited_date).format('YYYY-MM-DD') : '';
    const auditedDateChanged  = auditedDate !== originalAuditedDate;

    // ── Render helpers ─────────────────────────────────────────────────────────
    function renderEvidenceList(editable: boolean) {
        if (docsLoading) {
            return (
                <div className="cad-evidence-list">
                    {[1, 2].map(n => (
                        <div key={n} style={{ height: 44, borderRadius: 8, background: '#f3f4f6' }} />
                    ))}
                </div>
            );
        }
        // When there are no existing docs AND no upload in-flight, show the empty state.
        // Guard must come BEFORE the upload-in-flight check so the spinner always renders
        // even when the evidence list is empty (i.e. the very first upload to an activity).
        if ((!documents || documents.length === 0) && !(uploading && pendingFileName)) {
            return <p className="cad-empty-text">No files uploaded yet.</p>;
        }
        return (
            <div className="cad-evidence-list">
                {(documents || []).map((doc: any) => (
                    <div key={doc.id} className="cad-evidence-item">
                        <span className="cad-evidence-name" title={doc.file_name}>
                            {doc.file_name}
                        </span>
                        {doc.evidence_type === 'automated' && (
                            <span className="cad-auto-tag">Auto Attached</span>
                        )}
                        <div className="cad-evidence-actions">
                            <button
                                className="cad-evidence-btn"
                                title="Download"
                                onClick={() => downloadWithPresignedUrl(doc.file_name, doc.file_path)}
                            >
                                <img src={iconDownload} width="18" height="18" alt="Download" />
                            </button>
                            {editable && (
                                <button
                                    className="cad-evidence-btn danger"
                                    title="Delete"
                                    disabled={deleting as boolean}
                                    onClick={() => deleteDocument(doc.id)}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e53e3e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="3 6 5 6 21 6"/>
                                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                        <path d="M10 11v6M14 11v6"/>
                                        <path d="M9 6V4h6v2"/>
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>
                ))}
                {/* In-flight upload — shown as a pending row matching the evidence item style */}
                {uploading && pendingFileName && (
                    <div className="cad-evidence-item cad-evidence-item--uploading">
                        <span className="cad-evidence-name cad-evidence-name--uploading" title={pendingFileName}>
                            {pendingFileName}
                        </span>
                        <div className="cad-evidence-actions">
                            <span className="cad-uploading-label">Uploading…</span>
                            {/* Spinning ring */}
                            <svg className="cad-uploading-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2965ad" strokeWidth="2.5" strokeLinecap="round">
                                <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                                <path d="M12 2a10 10 0 0 1 10 10" />
                            </svg>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    function renderStatusBadge() {
        if (!activity || !activity.status) return null;
        const statusLabel = STATUS_LABELS[activity.status] ? STATUS_LABELS[activity.status] : activity.status;
        return (
            <div className="cad-status-row">
                <span className="cad-status-lbl">Status</span>
                <span className={'cad-status-badge ' + statusBadgeClass(activity.status)}>
                    {statusLabel}
                </span>
            </div>
        );
    }

    function renderOwnerReviewTab() {
        return (
            <>

                {/* ── Recall Info — only when this compliance was recalled ── */}
                {activity && activity.status === 'Recalled' && activity.metadata && (
                    <div className="cad-recall-box">
                        <div className="cad-recall-box-header">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="1 4 1 10 7 10" />
                                <path d="M3.51 15a9 9 0 1 0 .49-3.45" />
                            </svg>
                            Recalled by Manager
                        </div>
                        <div className="cad-meta-grid" style={{ marginTop: 10 }}>
                            <div>
                                <div className="cad-meta-item-label">Recalled By</div>
                                <div className="cad-meta-item-value">
                                    {(activity.metadata.recalled_by) ||
                                     (activity.compliance_manager && activity.compliance_manager.name) ||
                                     '—'}
                                </div>
                            </div>
                            <div>
                                <div className="cad-meta-item-label">Recalled Date</div>
                                <div className="cad-meta-item-value">
                                    {activity.metadata.recalled_at
                                        ? dayjs(activity.metadata.recalled_at).format('DD-MM-YYYY')
                                        : '—'}
                                </div>
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <div className="cad-meta-item-label">Recall Remarks</div>
                                <div className="cad-meta-item-value">
                                    {activity.metadata.recall_remarks || '—'}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Form Name */}
                <div className="cad-form-group">
                    <label className="cad-form-label">Form Name</label>
                    <input
                        className="cad-input"
                        type="text"
                        value={getStr(activity, 'act_state_mapping', 'form_name')}
                        disabled
                    />
                </div>

                {/* Actual Completion Date — optional, but must not be a future date */}
                <div className="cad-form-group">
                    <label className="cad-form-label">
                        Actual Completion Date
                    </label>
                    <input
                        className="cad-input"
                        type="date"
                        value={auditedDate}
                        max={isEditMode ? dayjs().format('YYYY-MM-DD') : undefined}
                        disabled={!isEditMode}
                        onChange={e => setAuditedDate(e.target.value)}
                    />
                </div>

                <hr className="cad-divider" />

                {/* Upload zone — only in edit mode */}
                {isEditMode && (
                    <div className="cad-form-group">
                        <label className="cad-form-label">Upload Evidence</label>
                        <div
                            className="cad-upload-zone"
                            onDragOver={e => e.preventDefault()}
                            onDrop={handleFileDrop}
                            onClick={() => fileRef.current && fileRef.current.click()}
                        >
                            <div className="cad-upload-icon">
                                <svg width="31" height="31" viewBox="0 0 24 24" fill="none" stroke="#757575" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="16 16 12 12 8 16" />
                                    <line x1="12" y1="12" x2="12" y2="21" />
                                    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                                </svg>
                            </div>
                            <p className="cad-upload-text">Drag and drop files here or click to browse</p>
                            <span className="cad-upload-btn">Choose File</span>
                            <p className="cad-upload-hint">{UPLOAD_HINT}</p>
                        </div>
                        <input
                            ref={fileRef}
                            type="file"
                            accept={UPLOAD_ACCEPT}
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />
                        {/* Uploading state is shown as a pending row in the evidence list below */}
                    </div>
                )}

                {/* Evidence list */}
                <div className="cad-form-group">
                    <label className="cad-form-label">Uploaded Evidences</label>
                    {renderEvidenceList(isEditMode)}
                </div>

                <hr className="cad-divider" />

                {/* Manager Remarks — read-only; shown at all stages so owner can see reviewer feedback */}
                {activity && activity.audit_remarks && (
                    <div className="cad-form-group">
                        <label className="cad-form-label">Manager Remarks</label>
                        <div className="cad-remarks-view">{activity.audit_remarks}</div>
                    </div>
                )}

                {/* Comments for Compliance Manager */}
                <div className="cad-form-group">
                    <label className={`cad-form-label${isEditMode ? ' required' : ''}`}>
                        Comments for Compliance Manager
                    </label>
                    <textarea
                        className="cad-textarea"
                        rows={3}
                        placeholder={isEditMode ? 'Enter your comments…' : ''}
                        value={ownerComment}
                        disabled={!isEditMode}
                        onChange={e => setOwnerComment(e.target.value)}
                    />
                </div>
            </>
        );
    }

    function renderManagerReviewTab() {
        // Resolve human-readable label for the Compliance Status view-mode field
        var rawStatusVal = activity && (activity.audit_status || activity.status);
        var statusViewLabel = rawStatusVal
            ? (STATUS_LABELS[rawStatusVal] ? STATUS_LABELS[rawStatusVal] : rawStatusVal)
            : '—';

        // Approved activity → show recall section only
        if (canManagerRecall) {
            return (
                <>
                    {/* Status badge already rendered once in the drawer header (line ~958);
                        don't duplicate it here. */}
                    <p className="cad-section-title">Recall Activity</p>
                    <p style={{ fontSize: 12, color: '#6b7280', fontFamily: 'Montserrat,sans-serif', marginBottom: 16 }}>
                        Recalling will revert this activity to the Owner for resubmission.
                    </p>

                    <div className="cad-form-group">
                        <label className={`cad-form-label${isEditMode ? ' required' : ''}`}>New Due Date</label>
                        <input
                            className="cad-input"
                            type="date"
                            value={recallDueDate}
                            disabled={!isEditMode}
                            onChange={e => setRecallDueDate(e.target.value)}
                        />
                    </div>

                    <div className="cad-form-group">
                        <label className={`cad-form-label${isEditMode ? ' required' : ''}`}>Recall Remarks</label>
                        <textarea
                            className="cad-textarea"
                            rows={3}
                            placeholder={isEditMode ? 'Enter reason for recalling this activity…' : ''}
                            value={recallRemarks}
                            disabled={!isEditMode}
                            onChange={e => setRecallRemarks(e.target.value)}
                        />
                    </div>

                    <hr className="cad-divider" />

                    {/* Proof of Compliance — Actual Completion Date is editable for the manager */}
                    <p className="cad-section-title">Proof of Compliance</p>

                    <div className="cad-form-group">
                        <label className={`cad-form-label${isEditMode ? ' required' : ''}`}>
                            Actual Completion Date
                        </label>
                        {isEditMode ? (
                            <input
                                className="cad-input"
                                type="date"
                                value={auditedDate}
                                max={dayjs().format('YYYY-MM-DD')}
                                onChange={e => setAuditedDate(e.target.value)}
                            />
                        ) : (
                            <div className="cad-meta-item-value" style={{ paddingTop: 4 }}>
                                {formatDate(getStr(activity, 'audited_date') === '—' ? null : (activity && activity.audited_date))}
                            </div>
                        )}
                    </div>

                    <div className="cad-meta-grid">
                        <div>
                            <div className="cad-meta-item-label">Owner Comments</div>
                            <div className="cad-meta-item-value cad-meta-item-value--multiline">{getStr(activity, 'forms_status_remarks')}</div>
                        </div>
                        <div>
                            <div className="cad-meta-item-label">Manager Remarks</div>
                            <div className="cad-meta-item-value cad-meta-item-value--multiline">{getStr(activity, 'audit_remarks')}</div>
                        </div>
                    </div>

                    <div className="cad-form-group" style={{ marginTop: 8 }}>
                        <label className="cad-form-label">Evidences</label>
                        {renderEvidenceList(false)}
                    </div>
                </>
            );
        }

        // Standard review section (Submitted, Rejected, Due, NonCompliant, Recalled, etc.)
        return (
            <>
                {/* ── Recall Info — shown to manager and escalation for Recalled activities ── */}
                {activity && activity.status === 'Recalled' && activity.metadata && (
                    <div className="cad-recall-box">
                        <div className="cad-recall-box-header">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="1 4 1 10 7 10" />
                                <path d="M3.51 15a9 9 0 1 0 .49-3.45" />
                            </svg>
                            Recalled by Manager
                        </div>
                        <div className="cad-meta-grid" style={{ marginTop: 10 }}>
                            <div>
                                <div className="cad-meta-item-label">Recalled By</div>
                                <div className="cad-meta-item-value">
                                    {(activity.metadata.recalled_by) ||
                                     (activity.compliance_manager && activity.compliance_manager.name) ||
                                     '—'}
                                </div>
                            </div>
                            <div>
                                <div className="cad-meta-item-label">Recalled Date</div>
                                <div className="cad-meta-item-value">
                                    {activity.metadata.recalled_at
                                        ? dayjs(activity.metadata.recalled_at).format('DD-MM-YYYY')
                                        : '—'}
                                </div>
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <div className="cad-meta-item-label">Recall Remarks</div>
                                <div className="cad-meta-item-value">
                                    {activity.metadata.recall_remarks || '—'}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Owner submission info */}
                <p className="cad-section-title">Proof of Compliance</p>

                {/* Actual Completion Date — editable by the Compliance Manager while reviewing */}
                <div className="cad-form-group">
                    <label className={`cad-form-label${isEditMode ? ' required' : ''}`}>
                        Actual Completion Date
                    </label>
                    {isEditMode ? (
                        <input
                            className="cad-input"
                            type="date"
                            value={auditedDate}
                            max={dayjs().format('YYYY-MM-DD')}
                            onChange={e => setAuditedDate(e.target.value)}
                        />
                    ) : (
                        <div className="cad-meta-item-value" style={{ paddingTop: 4 }}>
                            {formatDate(getStr(activity, 'audited_date') === '—' ? null : (activity && activity.audited_date))}
                        </div>
                    )}
                </div>

                <div className="cad-meta-grid">
                    <div>
                        <div className="cad-meta-item-label">Owner Comments</div>
                        <div className="cad-meta-item-value cad-meta-item-value--multiline">
                            {getStr(activity, 'forms_status_remarks')}
                        </div>
                    </div>
                    <div>
                        <div className="cad-meta-item-label">Form Name</div>
                        <div className="cad-meta-item-value">
                            {getStr(activity, 'act_state_mapping', 'form_name')}
                        </div>
                    </div>
                </div>

                <hr className="cad-divider" />

                {/* Evidences uploaded by owner */}
                <div className="cad-form-group">
                    <label className="cad-form-label">Evidences</label>
                    {renderEvidenceList(false)}
                </div>

                <hr className="cad-divider" />

                {/* Manager review inputs */}
                <p className="cad-section-title">Review</p>

                {/* Compliance Status — only shown in edit mode; status badge in the drawer header already displays it in view mode */}
                {isEditMode && (
                    <div className="cad-form-group">
                        <label className="cad-form-label required">
                            Compliance Status
                        </label>
                        <select
                            className="cad-select"
                            value={reviewStatus}
                            onChange={e => setReviewStatus(e.target.value)}
                        >
                            {REVIEW_STATUS_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="cad-form-group">
                    <label className={`cad-form-label${isEditMode ? ' required' : ''}`}>
                        Remarks by Compliance Manager
                    </label>
                    <textarea
                        className="cad-textarea"
                        rows={3}
                        placeholder={isEditMode ? 'Enter your review remarks…' : ''}
                        value={auditRemarks}
                        disabled={!isEditMode}
                        onChange={e => setAuditRemarks(e.target.value)}
                    />
                </div>
            </>
        );
    }

    function renderActTab() {
        return (
            <ul className="cad-field-list">
                <li>
                    <span className="cad-field-label">Act Name</span>
                    <span className="cad-field-value">{getStr(activity, 'act', 'name')}</span>
                </li>
                <li>
                    <span className="cad-field-label">Law Category</span>
                    <span className="cad-field-value">{getStr(activity, 'act', 'law', 'name')}</span>
                </li>
                <li>
                    <span className="cad-field-label">Establishment Type</span>
                    <span className="cad-field-value">{getStr(activity, 'act', 'establishment_type')}</span>
                </li>
                <li>
                    <span className="cad-field-label">Company</span>
                    <span className="cad-field-value">{getStr(activity, 'company', 'name')}</span>
                </li>
                <li>
                    <span className="cad-field-label">Associate Company</span>
                    <span className="cad-field-value">{getStr(activity, 'associate_company', 'name')}</span>
                </li>
                <li>
                    <span className="cad-field-label">Location</span>
                    <span className="cad-field-value">{getStr(activity, 'location', 'name')}</span>
                </li>
            </ul>
        );
    }

    function renderRuleTab() {
        var rcd = activity && activity.act_state_mapping && activity.act_state_mapping.rule_compliance_detail;

        return (
            <ul className="cad-field-list">
                <li>
                    <span className="cad-field-label">Rule Name</span>
                    <span className="cad-field-value">{getStr(activity, 'rule', 'name')}</span>
                </li>
                <li>
                    <span className="cad-field-label">Rule No.</span>
                    <span className="cad-field-value">{getStr(activity, 'rule', 'rule_no')}</span>
                </li>
                <li>
                    <span className="cad-field-label">Section No.</span>
                    <span className="cad-field-value">{getStr(activity, 'rule', 'section_no')}</span>
                </li>
                <li>
                    <span className="cad-field-label">Description</span>
                    <span className="cad-field-value">{getStr(activity, 'rule', 'description')}</span>
                </li>
                {rcd && rcd.compliance_description && (
                    <li>
                        <span className="cad-field-label">Compliance Description</span>
                        <span className="cad-field-value">{rcd.compliance_description}</span>
                    </li>
                )}
                <li>
                    <span className="cad-field-label">Risk</span>
                    <span className="cad-field-value">{rcd && rcd.risk ? rcd.risk : '—'}</span>
                </li>
                <li>
                    <span className="cad-field-label">Audit Type</span>
                    <span className="cad-field-value">{rcd && rcd.audit_type ? rcd.audit_type : '—'}</span>
                </li>
                {rcd && rcd.audit_type === 'Statutory' && (
                    <li>
                        <span className="cad-field-label">Statutory Authority</span>
                        <span className="cad-field-value">{rcd.statutory_authority ? rcd.statutory_authority : '—'}</span>
                    </li>
                )}
                <li>
                    <span className="cad-field-label">Penalty</span>
                    <span className="cad-field-value">{rcd && rcd.penalty ? rcd.penalty : '—'}</span>
                </li>
                <li>
                    <span className="cad-field-label">Max Penalty Amount</span>
                    <span className="cad-field-value">
                        {rcd && rcd.maximum_penalty_amount != null ? String(rcd.maximum_penalty_amount) : '—'}
                    </span>
                </li>
                <li>
                    <span className="cad-field-label">Proof Of Compliance</span>
                    <span className="cad-field-value">{rcd && rcd.proof_of_compliance ? rcd.proof_of_compliance : '—'}</span>
                </li>
                <li>
                    <span className="cad-field-label">Form Name</span>
                    <span className="cad-field-value">{getStr(activity, 'act_state_mapping', 'form_name')}</span>
                </li>
                <li>
                    <span className="cad-field-label">Imprisonment</span>
                    <span className="cad-field-value">{formatBool(rcd && rcd.impriosonment)}</span>
                </li>
                <li>
                    <span className="cad-field-label">Continuing Penalty</span>
                    <span className="cad-field-value">{formatBool(rcd && rcd.continuing_penalty)}</span>
                </li>
                <li>
                    <span className="cad-field-label">Cancellation / Suspension of License</span>
                    <span className="cad-field-value">{formatBool(rcd && rcd.cancellation_suspension_of_license)}</span>
                </li>
            </ul>
        );
    }

    function renderActivityTab() {
        return (
            <ul className="cad-field-list">
                <li>
                    <span className="cad-field-label">Activity Name</span>
                    <span className="cad-field-value">{getStr(activity, 'activity', 'name')}</span>
                </li>
                <li>
                    <span className="cad-field-label">Status</span>
                    <span className="cad-field-value">
                        {activity && activity.status
                            ? (STATUS_LABELS[activity.status] ? STATUS_LABELS[activity.status] : activity.status)
                            : '—'}
                    </span>
                </li>
                <li>
                    <span className="cad-field-label">Activity Type</span>
                    <span className="cad-field-value">{getStr(activity, 'activity', 'activity_type')}</span>
                </li>
                <li>
                    <span className="cad-field-label">Periodicity</span>
                    <span className="cad-field-value">{getStr(activity, 'activity', 'periodicity')}</span>
                </li>
                <li>
                    <span className="cad-field-label">Priority</span>
                    <span className="cad-field-value">{renderPriorityBadge(activity && activity.due_date)}</span>
                </li>
                <li>
                    <span className="cad-field-label">Due Date</span>
                    <span className="cad-field-value">{formatDate(activity && activity.due_date)}</span>
                </li>
                <li>
                    <span className="cad-field-label">Compliance Owner</span>
                    <span className="cad-field-value">{getStr(activity, 'compliance_owner', 'name')}</span>
                </li>
                <li>
                    <span className="cad-field-label">Compliance Manager</span>
                    <span className="cad-field-value">{getStr(activity, 'compliance_manager', 'name')}</span>
                </li>
                <li>
                    <span className="cad-field-label">Department</span>
                    <span className="cad-field-value">{getStr(activity, 'department', 'name')}</span>
                </li>
                <li>
                    <span className="cad-field-label">Vertical</span>
                    <span className="cad-field-value">{getStr(activity, 'vertical', 'name')}</span>
                </li>
            </ul>
        );
    }

    // ── Render ─────────────────────────────────────────────────────────────────
    const isOpen = Boolean(activity);

    return (
        <>
            {/* Overlay */}
            {isOpen && <div className="cad-overlay" onClick={onClose} />}

            {/* Drawer wrapper */}
            <div className={`cad-wrapper${isOpen ? ' open' : ''}`}>
                <div className="cad-panel">
                    {/* ── Header ── */}
                    <div className="cad-header">
                        <div className="cad-header-top">
                            <h2 className="cad-title">
                                {getStr(activity, 'activity', 'name')}
                            </h2>
                            {/* Icon actions: edit/view toggle + close — all grouped at top-right */}
                            <div className="cad-header-actions">
                                {canEdit && mode === 'view' && (
                                    <button className="cad-icon-btn" onClick={() => setMode('edit')} title="Edit">
                                        <img src={iconEdit} width="16" height="16" alt="Edit" />
                                    </button>
                                )}
                                {canEdit && mode === 'edit' && (
                                    <button className="cad-icon-btn" onClick={() => setMode('view')} title="View">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    </button>
                                )}
                                <button className="cad-close" onClick={onClose} title="Close">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                        <line x1="18" y1="6"  x2="6"  y2="18" />
                                        <line x1="6"  y1="6"  x2="18" y2="18" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Status badge — below title, above tabs (always visible regardless of active tab) */}
                        {renderStatusBadge()}

                        {/* Tab bar */}
                        <div className="cad-tabs">
                            {TABS.map(tab => (
                                <button
                                    key={tab.key}
                                    className={`cad-tab${activeTab === tab.key ? ' active' : ''}`}
                                    onClick={() => setActiveTab(tab.key)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* ── Body ── */}
                    <div className="cad-body">
                        {activity && (
                            <>
                                {activeTab === 'review' && (
                                    isOwner
                                        ? renderOwnerReviewTab()
                                        : renderManagerReviewTab()
                                )}
                                {activeTab === 'act'      && renderActTab()}
                                {activeTab === 'rule'     && renderRuleTab()}
                                {activeTab === 'activity' && renderActivityTab()}
                            </>
                        )}
                    </div>

                    {/* ── Footer — shown in edit mode on Review & Evidences tab ── */}
                    {isEditMode && activeTab === 'review' && (
                        <div className="cad-footer">
                            <button className="cad-btn-cancel" onClick={handleCancel}>
                                Cancel
                            </button>

                            {/* Approved activity: Save (update date, only enabled once it's changed) + Recall (revert to owner) */}
                            {canManagerRecall ? (
                                <>
                                    <button
                                        className="cad-btn-submit"
                                        disabled={submitting || !auditedDateChanged}
                                        onClick={handleManagerUpdateDate}
                                    >
                                        {submitting ? 'Saving…' : 'Save'}
                                    </button>
                                    <button
                                        className="cad-btn-recall"
                                        disabled={submitting}
                                        onClick={handleManagerRecall}
                                    >
                                        {submitting ? 'Recalling…' : 'Recall'}
                                    </button>
                                </>
                            ) : (
                                <button
                                    className="cad-btn-submit"
                                    disabled={submitting}
                                    onClick={isOwner ? handleOwnerSubmit : handleManagerSubmit}
                                >
                                    {submitting ? 'Submitting…' : 'Submit'}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Submit confirmation dialog (owner only) ───────────────────── */}
            {showSubmitConfirm && (
                <div className="cad-confirm-overlay">
                    <div className="cad-confirm-dialog">
                        <div className="cad-confirm-head">
                            <span className="cad-confirm-title">Submit Compliance Activity</span>
                            <button
                                className="cad-close"
                                onClick={() => setShowSubmitConfirm(false)}
                                aria-label="Close"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                        <div className="cad-confirm-body">
                            {(documents || []).length === 0 && (
                                <div className="cad-confirm-warn">
                                    There are no supporting documents uploaded to this activity!
                                </div>
                            )}
                            <p className="cad-confirm-msg">
                                Are you sure you want to submit this compliance activity?
                                Please review all the attached evidences/documents before submitting.
                            </p>
                        </div>
                        <div className="cad-confirm-foot">
                            <button
                                className="cad-btn-cancel"
                                onClick={() => setShowSubmitConfirm(false)}
                            >
                                No
                            </button>
                            <button
                                className="cad-btn-submit"
                                onClick={handleConfirmOwnerSubmit}
                            >
                                Yes, Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
